(() => {
  if (customElements.get('mix-match-bundle')) return;

  class MixMatchBundle extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;
        this.selected = [];
        this.tierSelections = new Map();
        this.busy = false;
        this.uncertain = false;
        this.status = this.querySelector('.mmb__status');
        try {
          this.formatter = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency });
        } catch {
          this.formatter = new Intl.NumberFormat('en', { style: 'currency', currency: this.dataset.currency || 'USD' });
        }
        this.cards = Array.from(this.querySelectorAll('.mmb__card')).map((el) => {
          const product = JSON.parse(el.querySelector('[data-product-json]').textContent);
          return { el, product, modal: el.querySelector('quick-view'), variant: product.variants.find((variant) => variant.available) };
        }).filter((card) => card.variant);
        this.tier = this.querySelector('[data-tier]');
        this.visibleProducts = new Map();
        this.querySelectorAll('[data-panel]').forEach((panel) => {
          this.visibleProducts.set(panel, 4);
          this.updateVisibleProducts(panel);
        });
      }

      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.breakpoint ||= window.matchMedia('(max-width: 767px)');
      this.breakpoint.addEventListener('change', () => {
        this.render();
        this.updateCatalogFade();
        this.centerSelectedTab('instant');
      }, options);
      this.addEventListener('click', (event) => this.handleClick(event), options);
      this.addEventListener('keydown', (event) => this.onTabKey(event), options);
      this.pickerObserver = new MutationObserver(() => this.updatePicker());
      this.cards.forEach((card) => {
        const modal = card.modal;
        if (!modal) return;
        // QuickView moves to document.body while open; bind to the modal itself.
        modal.classList.add('mmb__quick-view');
        modal.addEventListener('submit', (event) => this.onBundleVariantSubmit(event), { ...options, capture: true });
        modal.addEventListener('click', (event) => this.onPickerProductClick(event), { ...options, capture: true });
        modal.addEventListener('change', (event) => this.onPickerProductChange(event), { ...options, capture: true });
        modal.addEventListener('change', () => this.updatePicker(), options);
        modal.addEventListener('variant:change', () => this.updatePicker(), { ...options, capture: true });
        this.pickerObserver.observe(modal, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
      });
      this.sectionSelect = (event) => {
        const tab = this.querySelector(`[data-tier="${event.detail.blockId}"]`);
        if (tab && !this.busy && !this.uncertain) this.changeTier(tab);
      };
      this.closest('.shopify-section')?.addEventListener('shopify:block:select', this.sectionSelect, options);
      this.catalogObserver = new ResizeObserver(() => this.updateCatalogFade());
      const catalog = this.querySelector('.mmb__catalog');
      this.setupCatalogScrollbar(catalog, options);
      catalog.addEventListener('scroll', () => this.updateCatalogFade(), { ...options, passive: true });
      this.catalogObserver.observe(catalog);
      this.querySelectorAll('.mmb__grid').forEach((grid) => this.catalogObserver.observe(grid));
      this.render();
      this.updateCatalogFade();
      this.centerSelectedTab('instant');
    }

    disconnectedCallback() {
      this.events?.abort();
      this.catalogObserver?.disconnect();
      this.pickerObserver?.disconnect();
      this.cards?.forEach((card) => card.modal?.bundleRequest?.abort());
      this.picker?.hide();
      this.pickerCard = null;
      if (this.busy) {
        // A disconnected cart write may already have reached Shopify.
        this.uncertain = true;
        this.busy = false;
      }
      this.request?.abort();
    }

    money(cents) { return this.formatter.format(cents / 100); }
    get count() { return this.tier ? Number(this.tier.dataset.count) : 3; }
    get full() { return !!this.tier && this.selected.length >= this.count; }
    get mobile() { return this.breakpoint.matches; }

    unavailableReason(card) {
      const variant = card.variant;
      if (!this.tier || !variant.available || this.busy || this.uncertain) return 'Unavailable';
      if (variant.min > 1 || variant.increment > 1) return 'Quantity rule unavailable';
      if (this.dataset.repeat !== 'true' && this.selected.some((item) => item.variant.id === variant.id)) return 'Added to Bundle';
      if (this.full) return 'Bundle Full';
      return '';
    }

    updateCatalogFade() {
      const catalog = this.querySelector('.mmb__catalog');
      const fade = this.querySelector('.mmb__catalog-fade');
      fade.hidden = catalog.scrollHeight - catalog.clientHeight - catalog.scrollTop <= 1;
      const track = this.querySelector('.mmb__catalog-scrollbar');
      if (!track) return;
      const range = Math.max(0, catalog.scrollHeight - catalog.clientHeight);
      track.hidden = this.mobile || range <= 1;
      if (track.hidden) return;
      const height = track.clientHeight;
      const thumbHeight = Math.min(height, Math.max(32, height * catalog.clientHeight / catalog.scrollHeight));
      const thumb = track.firstElementChild;
      thumb.style.height = `${thumbHeight}px`;
      thumb.style.transform = `translateY(${Math.max(0, Math.min(1, catalog.scrollTop / range)) * (height - thumbHeight)}px)`;
    }

    setupCatalogScrollbar(catalog, options) {
      const shell = catalog.parentElement;
      let track = shell.querySelector('.mmb__catalog-scrollbar');
      if (!track) {
        track = document.createElement('div');
        track.className = 'mmb__catalog-scrollbar';
        // The catalog remains focusable for native keyboard scrolling.
        track.setAttribute('aria-hidden', 'true');
        const thumb = document.createElement('div');
        thumb.className = 'mmb__catalog-scrollbar-thumb';
        track.append(thumb);
        shell.append(track);
      }
      shell.setAttribute('data-scrollbar-ready', '');
      let drag = null;
      track.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        const thumb = track.firstElementChild;
        const travel = track.clientHeight - thumb.offsetHeight;
        const range = catalog.scrollHeight - catalog.clientHeight;
        if (travel <= 0 || range <= 0) return;
        if (event.target !== thumb) {
          catalog.scrollTop = (event.clientY - track.getBoundingClientRect().top - thumb.offsetHeight / 2) / travel * range;
        }
        drag = { pointerId: event.pointerId, y: event.clientY, top: catalog.scrollTop, ratio: range / travel };
        track.setPointerCapture(event.pointerId);
        this.updateCatalogFade();
      }, options);
      track.addEventListener('pointermove', (event) => {
        if (drag?.pointerId !== event.pointerId) return;
        catalog.scrollTop = drag.top + (event.clientY - drag.y) * drag.ratio;
      }, options);
      track.addEventListener('lostpointercapture', () => { drag = null; }, options);
    }

    updateVisibleProducts(panel) {
      const cards = [...panel.querySelectorAll('.mmb__card')];
      const visible = this.visibleProducts.get(panel) || 4;
      cards.forEach((card, index) => card.toggleAttribute('data-mobile-hidden', index >= visible));
      const more = panel.querySelector('[data-view-more]');
      if (more) {
        const allVisible = visible >= cards.length;
        more.hidden = cards.length <= 4;
        more.dataset.collapse = String(allVisible);
        more.setAttribute('aria-expanded', String(visible > 4));
        more.querySelector('[data-view-label]').textContent = allVisible ? 'View Less' : 'View More';
      }
    }

    centerSelectedTab(behavior = 'smooth') {
      if (!this.mobile || !this.tier) return;
      const tabs = this.tier.closest('.mmb__tabs');
      const bounds = tabs.getBoundingClientRect();
      const tabBounds = this.tier.getBoundingClientRect();
      tabs.scrollBy({
        left: tabBounds.left + tabBounds.width / 2 - bounds.left - tabs.clientWidth / 2,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : behavior,
      });
    }

    changeTier(tab) {
      if (tab === this.tier) {
        this.centerSelectedTab();
        return;
      }
      if (this.tier) this.tierSelections.set(this.tier.dataset.tier, this.selected);
      this.picker?.hide();
      this.tier = tab;
      this.selected = this.tierSelections.get(tab.dataset.tier) || [];
      this.querySelectorAll('[data-tier]').forEach((button) => {
        button.setAttribute('aria-selected', String(button === tab));
        button.tabIndex = button === tab ? 0 : -1;
      });
      this.querySelectorAll('[data-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.panel !== tab.dataset.tier;
      });
      const catalog = this.querySelector('.mmb__catalog');
      if (catalog) catalog.scrollTop = 0;
      this.status.textContent = `Offer changed. ${this.selected.length} of ${this.count} selected.`;
      this.render();
      this.updateCatalogFade();
      this.centerSelectedTab();
    }

    onTabKey(event) {
      const tab = event.target.closest('[data-tier]');
      if (!tab || this.busy || this.uncertain) return;
      const tabs = [...this.querySelectorAll('[data-tier]')];
      let index = tabs.indexOf(tab);
      const direction = document.documentElement.dir === 'rtl' ? -1 : 1;
      if (event.key === 'ArrowRight') index += direction;
      else if (event.key === 'ArrowLeft') index -= direction;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = tabs.length - 1;
      else return;
      event.preventDefault();
      const next = tabs[(index + tabs.length) % tabs.length];
      this.changeTier(next);
      next.focus({ preventScroll: this.mobile });
      if (!this.mobile) next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    addCard(card) {
      if (this.unavailableReason(card)) return false;
      // Retain a snapshot: choosing another variant must not mutate selected items.
      this.selected.push({ product: card.product, variant: card.variant });
      this.status.textContent = `${card.product.title} added. ${this.selected.length} of ${this.count} selected.`;
      this.render();
      return true;
    }

    openPicker(card) {
      this.pickerCard = card;
      this.picker = card.modal;
      this.picker.show(card.el.querySelector('.mmb__add'));
    }

    pickerProductUrl(target, modal) {
      const value = target?.getAttribute('data-product-url');
      const info = target?.closest('product-info');
      if (!value || !info || !modal.contains(info)) return null;
      const url = new URL(value, window.location.origin);
      const current = new URL(info.dataset.productUrl, window.location.origin);
      return url.origin === current.origin && url.pathname !== current.pathname ? url : null;
    }

    onPickerProductClick(event) {
      const modal = event.currentTarget;
      const link = event.target.closest('.variant-picker a[href]');
      const input = event.target.closest('variant-picker input[data-product-url]');
      const url = link ? new URL(link.href, window.location.origin) : this.pickerProductUrl(input, modal);
      if (!url || url.origin !== window.location.origin) return;
      if (link && !url.pathname.includes('/products/')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.loadPickerProduct(modal, url);
    }

    onPickerProductChange(event) {
      const modal = event.currentTarget;
      const target = event.target.tagName === 'SELECT' ? event.target.selectedOptions[0] : event.target;
      const url = this.pickerProductUrl(target, modal);
      if (!url && !modal.bundleRequest) return;
      // Stop before variant-picker publishes to ProductInfo, which redirects across products.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (url) this.loadPickerProduct(modal, url);
    }

    async loadPickerProduct(modal, url) {
      modal.bundleRequest?.abort();
      const request = new AbortController();
      modal.bundleRequest = request;
      // Discard any in-flight same-product variant update before replacing its markup.
      modal.querySelectorAll('product-info').forEach((info) => info.abortController?.abort());
      modal.setAttribute('aria-busy', 'true');
      this.updatePicker();
      try {
        const requestUrl = new URL(url);
        requestUrl.searchParams.set('view', 'modal');
        const response = await fetch(requestUrl, { signal: request.signal });
        if (!response.ok) throw new Error('Product could not be loaded. Please try again.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = html.querySelector('.quick-view__content');
        const product = JSON.parse(content?.querySelector('[data-bundle-product-json]')?.textContent || 'null');
        if (!product?.variants?.length || !content.querySelector('form[is="product-form"]')) {
          throw new Error('Product could not be loaded. Please try again.');
        }
        if (request.signal.aborted || !this.isConnected || !modal.hasAttribute('open')) return;
        modal.setInnerHTML(modal.querySelector('.quick-view__content'), content.innerHTML);
        window.Shopify?.PaymentButton?.init();
        theme.a11y.trapFocus(modal, modal.focusElement);
        const focus = modal.querySelector('variant-picker input:checked, variant-picker select, form[is="product-form"] button[type="submit"]');
        focus?.focus({ preventScroll: true });
        document.dispatchEvent(new CustomEvent('quickview:loaded', { detail: { productUrl: url.href } }));
      } catch (error) {
        if (request.signal.aborted || !modal.hasAttribute('open')) return;
        // Keep the previous product usable when loading the related product fails.
        modal.querySelectorAll('variant-picker input[type="radio"]').forEach((input) => { input.checked = input.defaultChecked; });
        modal.querySelectorAll('variant-picker option').forEach((option) => { option.selected = option.defaultSelected; });
        modal.querySelector('form[is="product-form"]')?.handleErrorMessage?.('Product could not be loaded. Please try again.');
      } finally {
        if (modal.bundleRequest === request) {
          modal.bundleRequest = null;
          modal.removeAttribute('aria-busy');
          this.updatePicker();
        }
      }
    }

    getPickerSelection(card) {
      const modal = card.modal;
      const source = modal.querySelector('[data-bundle-product-json]');
      if (source !== modal.bundleProductSource) {
        modal.bundleProduct = source ? JSON.parse(source.textContent) : null;
        modal.bundleProductSource = source;
      }
      const product = modal.bundleProduct || card.product;
      const variantId = Number(modal.querySelector('form[is="product-form"] [name="id"]')?.value);
      return { product, variant: product.variants.find((item) => item.id === variantId) };
    }

    updatePicker() {
      this.cards.forEach(({ modal }) => {
        if (modal && !modal.hasAttribute('open') && modal.bundleRequest) {
          modal.bundleRequest.abort();
          modal.bundleRequest = null;
          modal.removeAttribute('aria-busy');
        }
      });
      const openCard = this.cards.find((card) => card.modal?.hasAttribute('open'));
      if (openCard) {
        this.pickerCard = openCard;
        this.picker = openCard.modal;
      }
      if (!this.pickerCard || !this.picker) return;
      const form = this.picker.querySelector('form[is="product-form"]');
      if (!form) return;
      const selection = this.getPickerSelection(this.pickerCard);
      const { variant } = selection;
      const reason = this.picker.bundleRequest ? 'Loading…' : variant ? this.unavailableReason(selection) : 'Unavailable';
      const button = form.querySelector('[type="submit"]');
      if (!button) return;
      button.disabled = !!reason;
      button.setAttribute('aria-disabled', String(!!reason));
      button.toggleAttribute('data-bundle-disabled', !!reason && !!variant?.available);
      const label = button.querySelector('.btn-text') || button;
      const text = this.picker.bundleRequest ? 'Loading…' : this.full && variant?.available ? 'Add to Bundle' : reason || 'Add to Bundle';
      if (label.textContent !== text) label.textContent = text;
    }

    onBundleVariantSubmit(event) {
      const form = event.target;
      const modal = event.currentTarget;
      if (!form.matches('form[is="product-form"]')) return;
      // Intercept before the theme product form can send a real cart request.
      event.preventDefault();
      event.stopImmediatePropagation();
      const card = this.cards.find((item) => item.modal === modal);
      if (!card || modal.bundleRequest) return;
      const selection = this.getPickerSelection(card);
      if (!selection.variant) return;
      if (this.addCard(selection)) {
        modal.hide();
      }
    }

    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || button.disabled) return;
      if (button.matches('[data-summary-toggle]')) {
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(expanded));
        this.querySelector('.mmb__sidebar').dataset.expanded = String(expanded);
        return;
      }
      if (button.matches('[data-view-more]')) {
        const panel = button.closest('[data-panel]');
        const collapse = button.dataset.collapse === 'true';
        this.visibleProducts.set(panel, collapse ? 4 : panel.querySelectorAll('.mmb__card').length);
        this.updateVisibleProducts(panel);
        if (collapse && this.mobile) this.scrollIntoView({ block: 'start', behavior: 'instant' });
        return;
      }
      if (this.busy || this.uncertain) return;
      if (button.matches('[data-tier]')) return this.changeTier(button);
      if (button.matches('.mmb__remove')) {
        const index = Number(button.dataset.index);
        this.selected.splice(index, 1);
        this.status.textContent = `Item removed. ${this.selected.length} of ${this.count} selected.`;
        this.render();
        const toggle = this.querySelector('[data-summary-toggle]');
        const next = this.querySelectorAll('.mmb__remove')[index] || (toggle.disabled ? this.tier : toggle);
        if (next?.getClientRects().length) next.focus({ preventScroll: this.mobile });
        return;
      }
      if (button.matches('.mmb__submit')) return this.submit();
      const card = this.cards.find((item) => item.el === button.closest('.mmb__card'));
      if (!card) return;
      if (button.matches('.mmb__add')) {
        if (card.product.hasOptions) this.openPicker(card);
        else this.addCard(card);
      }
    }

    render() {
      const subtotal = this.selected.reduce((sum, item) => sum + item.variant.price, 0);
      const price = this.tier ? Number(this.tier.dataset.price) : 0;
      const total = this.full ? price : subtotal;
      const compareTotal = this.selected.reduce((sum, item) => sum + Math.max(item.variant.compare || 0, item.variant.price), 0);
      this.querySelector('[data-total-label]').textContent = 'Total:';
      this.querySelector('[data-total]').textContent = this.selected.length
        ? `${this.money(total)} ${this.dataset.currency}`
        : '--';
      const compare = this.querySelector('[data-total-compare]');
      compare.hidden = !this.selected.length || compareTotal <= total;
      compare.textContent = compare.hidden ? '' : this.money(compareTotal);
      this.querySelector('[data-offer]').textContent = this.tier
        ? `Offer: ${this.count} items for ${this.money(price)}. ${this.selected.length}/${this.count} selected.`
        : 'No offer configured.';
      const submit = this.querySelector('.mmb__submit');
      submit.disabled = !this.full || this.busy || this.uncertain;
      this.querySelector('[data-submit-desktop]').textContent = this.busy ? 'Adding…' : this.full || !this.selected.length ? 'Add to cart' : `Bundle: ${this.selected.length}/${this.count} Items`;
      this.querySelector('[data-submit-mobile]').textContent = this.selected.length
        ? `${this.money(this.full ? price : subtotal)} ${this.dataset.currency} ｜ ${this.busy ? 'Adding…' : 'Add to cart'}`
        : 'Add to cart';
      this.querySelectorAll('[data-tier]').forEach((tab) => { tab.disabled = this.busy || this.uncertain; });
      this.querySelector('[data-deal-heading]').textContent = !this.tier ? 'No Offer Available' : this.selected.length
        ? `Bundle: ${this.selected.length}/${this.count} Items`
        : `Choose ${this.count} Items to Unlock Your Deal`;
      this.querySelector('[data-bundle-complete]').hidden = !this.full;
      const sidebar = this.querySelector('.mmb__sidebar');
      sidebar.dataset.full = String(this.full);
      sidebar.dataset.empty = String(!this.selected.length);
      const toggle = this.querySelector('[data-summary-toggle]');
      toggle.disabled = !this.selected.length;
      if (!this.selected.length) {
        toggle.setAttribute('aria-expanded', 'false');
        sidebar.dataset.expanded = 'false';
      }
      const progress = this.querySelector('[data-selection-progress]');
      progress.setAttribute('aria-valuemax', this.count);
      progress.setAttribute('aria-valuenow', this.selected.length);
      progress.setAttribute('aria-valuetext', `${this.selected.length} of ${this.count} items selected`);
      if (progress.children.length !== this.count) {
        progress.replaceChildren(...Array.from({ length: this.count }, () => {
          const segment = document.createElement('span');
          segment.setAttribute('aria-hidden', 'true');
          return segment;
        }));
      }
      [...progress.children].forEach((segment, index) => segment.classList.toggle('is-filled', index < this.selected.length));
      this.renderSelected();
      this.cards.forEach((card) => {
        const button = card.el.querySelector('.mmb__add');
        const reason = this.unavailableReason(card);
        // Another variant may still be eligible, so keep the picker accessible.
        const canChoose = card.product.hasOptions && !this.busy && !this.uncertain && !this.full && !!this.tier;
        button.disabled = !!reason && !canChoose;
        const text = canChoose || this.full ? 'Add to Bundle' : reason || 'Add to Bundle';
        card.el.querySelector('[data-add-desktop]').textContent = text;
        card.el.querySelector('[data-add-mobile]').textContent = text;

      });
      this.updatePicker();
    }

    renderSelected() {
      const list = this.querySelector('.mmb__selected');
      const fragment = document.createDocumentFragment();
      if (!this.selected.length) {
        const empty = this.querySelector('[data-empty-selection]').content.cloneNode(true);
        empty.querySelector('[data-empty-count]').textContent = `Choose ${this.count} Items to`;
        fragment.append(empty);
      }
      this.selected.forEach((item, index) => {
        const row = document.createElement('div');
        row.className = 'mmb__row';
        const image = document.createElement('img');
        image.alt = '';
        image.loading = 'lazy';
        const src = item.variant.image || item.product.image;
        if (src) image.src = src;
        const details = document.createElement('div');
        details.className = 'mmb__row-details';
        const title = document.createElement('p');
        title.className = 'mmb__row-title';
        title.textContent = item.product.title;
        const variant = document.createElement('p');
        variant.className = 'mmb__row-variant';
        const desktopVariant = document.createElement('span');
        desktopVariant.className = 'mmb__desktop-label';
        desktopVariant.textContent = item.variant.title === 'Default Title' ? '' : `Select ${item.product.optionLabel || 'Option'}: ${item.variant.title}`;
        const mobileVariant = document.createElement('span');
        mobileVariant.className = 'mmb__mobile-label';
        mobileVariant.textContent = item.variant.title === 'Default Title' ? '' : item.variant.title;
        variant.append(desktopVariant, mobileVariant);
        const price = document.createElement('strong');
        price.textContent = this.money(item.variant.price);
        const prices = document.createElement('div');
        prices.className = 'mmb__row-prices';
        prices.append(price);
        if (item.variant.compare > item.variant.price) {
          const comparePrice = document.createElement('del');
          comparePrice.textContent = this.money(item.variant.compare);
          prices.append(comparePrice);
        }
        details.append(title, variant, prices);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'mmb__remove';
        remove.dataset.index = index;
        remove.setAttribute('aria-label', `Remove ${item.product.title}`);
        remove.append(this.querySelector('[data-remove-icon]').content.cloneNode(true));
        remove.disabled = this.busy || this.uncertain;
        row.append(image, details, remove);
        fragment.append(row);
      });
      list.replaceChildren(fragment);
    }

    async submit() {
      if (this.busy || this.uncertain || !this.full) return;
      this.busy = true;
      this.status.textContent = '';
      this.render();
      const grouped = new Map();
      this.selected.forEach((item) => grouped.set(item.variant.id, (grouped.get(item.variant.id) || 0) + 1));
      const items = Array.from(grouped, ([id, quantity]) => ({ id, quantity }));
      const root = (window.Shopify?.routes?.root || this.dataset.root || '/').replace(/\/?$/, '/');
      this.request = new AbortController();
      try {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }), signal: this.request.signal,
        });
        if (!response.ok) {
          let data = {};
          try { data = await response.json(); } catch {}
          throw new Error(data.description || data.message || 'The items could not be added.');
        }
        const added = await response.json();
        const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, signal: this.request.signal });
        if (!cartResponse.ok) throw new Error('The items were added, but the cart could not be refreshed.');
        const cart = await cartResponse.json();
        cart.sections = added.sections;
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'mix-match-bundle', cart });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: added } }));
        if (!this.isConnected) return;
        this.selected = [];
        this.tierSelections.set(this.tier.dataset.tier, this.selected);
        this.busy = false;
        this.render();
        const drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.show(this.querySelector('.mmb__submit'));
        else window.location.assign(this.dataset.cart || `${root}cart`);
      } catch (error) {
        this.busy = false;
        this.uncertain = true;
        this.status.textContent = `${error.name === 'AbortError' ? 'Cart update interrupted.' : error.message} Check your cart before rebuilding the bundle to avoid duplicate items. Reload this page to start again.`;
        this.status.classList.remove('mmb__sr');
        this.querySelector('.mmb__cart-link').hidden = false;
        if (this.isConnected) this.render();
      }
    }
  }

  customElements.define('mix-match-bundle', MixMatchBundle);
})();
